import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import type { GetStaticPathsResult, GetStaticPropsContext } from 'next';
import Head from 'next/head';
import { Content } from '@/components/Content/Content';
import { fetchFromClient, fetchFromServer } from '../../fetch';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Article from '../../components/Article/Article';
import { NewRequestDemoForm } from '@/components/NewRequestDemoForm/NewRequestDemoForm';
import { GreenButton } from '@/components/ButtonsCollection/GreenButton/GreenButton';
import { PATH_ARTICLES, PATH_AUTHORS, PATH_SUCCESS, PATH_TAGS } from '@/constants/spa-routes';
import Link from 'next/link';
import { Typography } from '@/components/Typography/Typography';
import { clsx } from 'clsx';
import { FullScreenQuidgetComponent } from '@/components/FullScreenQuidgetComponent/FullScreenQuidgetComponent';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Container } from '@/components/Containers/Container';
import Script from 'next/script';
import { PATH_ARTICLE_VIEWS } from '@/constants/api-routes';
import { EyeIcon } from '@/assets/icons/EyeIcon';
import { useRouter } from 'next/router';
import { NonExistedArticle } from '@/components/NonExistedArticle/NonExistedArticle';

type Media = {
  desktop: null | [];
  mobile: null | [];
};

type Articles = {
  id: string;
  attributes: {
    Title: string;
    Short_Description: string;
    publishedAt: string;
    views: string;
    URL: string;
    author: {
      data: {
        id: number;
        attributes: {
          Name: string;
          Position: string;
          Avatar: {
            data: {
              attributes: {
                url: string;
              };
            };
          };
        };
      };
    };
    Hero_Image: {
      data: {
        attributes: {
          url: string;
          alternativeText: string;
        };
      };
    };
    IMAGES_DESKTOP: {
      data: [
        {
          attributes: {
            alternativeText: string;
            url: string;
          };
        },
      ];
    };
    IMAGES_MOBILE: {
      data:
        | [
            {
              attributes: {
                alternativeText: string;
                url: string;
              };
            },
          ]
        | null;
    };
    VIDEOS_DESKTOP: {
      data:
        | [
            {
              attributes: {
                alternativeText: string;
                url: string;
              };
            },
          ]
        | null;
    };
    VIDEOS_MOBILE: {
      data:
        | [
            {
              attributes: {
                alternativeText: string;
                url: string;
              };
            },
          ]
        | null;
    };
    tags: {
      data: [
        {
          id: number;
          attributes: {
            Name: string;
          };
        },
      ];
    };
    OpenGraph: {
      OGTitle: string;
      OGDescription: string;
      OGImage: {
        data: {
          attributes: {
            url: string;
          };
        };
      };
    };
    HeaderTags: {
      TitleTag: string;
      DescriptionTag: string;
    };
    Form_Title: string;
    Form_Text: string;
  };
};

type BlogPageProps = {
  article: Articles;
  relatedArticles: [Articles];
  images: Media;
  videos: Media;
  error?: Error;
  originUrl: string;
  isMobile: boolean;
};
function getImages(article: Articles) {
  const images = {
    desktop: null,
    mobile: null,
  };

  if (article.attributes.IMAGES_DESKTOP.data) {
    // @ts-ignore
    images.desktop = article.attributes.IMAGES_DESKTOP.data.map((image) => ({
      alternativeText: image.attributes.alternativeText,
      url: image.attributes.url,
    }));
  }
  if (article.attributes.IMAGES_MOBILE.data) {
    // @ts-ignore
    images.mobile = article.attributes.IMAGES_MOBILE.data.map((image) => ({
      alternativeText: image.attributes.alternativeText,
      url: image.attributes.url,
    }));
  }

  return images;
}
function getVideos(article: Articles) {
  const videos = {
    desktop: null,
    mobile: null,
  };

  if (article.attributes.VIDEOS_DESKTOP.data) {
    // @ts-ignore
    videos.desktop = article.attributes.VIDEOS_DESKTOP.data.map((image) => ({
      alternativeText: image.attributes.alternativeText,
      url: image.attributes.url,
    }));
  }
  if (article.attributes.VIDEOS_MOBILE.data) {
    // @ts-ignore
    videos.mobile = article.attributes.VIDEOS_MOBILE.data.map((image) => ({
      alternativeText: image.attributes.alternativeText,
      url: image.attributes.url,
    }));
  }

  return videos;
}
function AuthorInfo({
  author,
  showPublishDate,
  formattedDate = null,
}: {
  author: Articles['attributes']['author'];
  showPublishDate?: boolean;
  formattedDate?: string | null;
}) {
  if (!author?.data) {
    return null;
  }

  const authorUrl = author?.data?.attributes?.Name?.toLowerCase().trim().replace(/\ /g, '-');

  return (
    <div className="article-author flex-1">
      <div className="article-author-avatar">
        <Link href={`${PATH_AUTHORS.ROOT}/${authorUrl}`} prefetch={false}>
          <Image
            src={author.data.attributes.Avatar.data.attributes.url}
            width={45}
            height={45}
            alt={author.data.attributes.Name}
            style={{ borderRadius: '50%' }}
          />
        </Link>
      </div>
      <div className="article-author-name">
        <p>
          by{' '}
          <Link
            prefetch={false}
            href={`${PATH_AUTHORS.ROOT}/${authorUrl}`}
            className="hover:underline"
          >
            {author.data.attributes.Name}
          </Link>
        </p>
        <p>
          <Link
            prefetch={false}
            href={`${PATH_AUTHORS.ROOT}/${authorUrl}`}
            className="!no-underline !font-medium"
          >
            <span className="grey">{author.data.attributes.Position}</span>
          </Link>
        </p>
        {showPublishDate && (
          <p className={'flex md:hidden'}>
            <PublishDate publishDate={formattedDate} />
          </p>
        )}
      </div>
    </div>
  );
}
function PublishDate({ publishDate }: { publishDate: string | null }) {
  if (!publishDate) {
    return null;
  }
  return <span className="published-date">{publishDate}</span>;
}

export default function BlogPage({
  article,
  relatedArticles,
  images,
  error,
  originUrl,
  videos,
}: BlogPageProps) {
  const [articleState, setArticleState] = useState(article);
  const [imagesState, setImagesState] = useState<Media>(images);
  const [videosState, setVideosState] = useState<Media>(videos);
  const [isUndefinedArticle, setIsUndefinedArticle] = useState(error || !article);
  const [submitted, setSubmitted] = useState(false);
  const [views, setViews] = useState(articleState?.attributes?.views || 0);
  const [articleViewsData, setArticleViewsData] = useState<null | number>(null);
  const [isScreenHeight, setIsScreenHeight] = useState<boolean | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const url = `${originUrl}${articleState?.attributes?.URL}`;
  const { push } = useRouter();
  const form = {
    title: articleState?.attributes.Form_Title || 'Want to learn how',
    text:
      articleState?.attributes.Form_Text ||
      'you can keep up with the latest trends and innovations in AI for knowledge-base management?',
  };
  const formDesktop = {
    title: articleState?.attributes.Form_Title || 'Ready to build a custom AI solution',
    text:
      articleState?.attributes.Form_Text || 'designer specifically for your business challenges?',
  };

  const updateArticleViewHandle = async () => {
    if (typeof articleViewsData === 'number') {
      try {
        await fetchFromClient('PUT', PATH_ARTICLE_VIEWS.ROOT, {
          views: (articleViewsData || 0) + 1,
          articleId: articleState.id,
        });

        sessionStorage.setItem('viewed_article', articleState.id);
        setViews((articleViewsData || 0) + 1);
      } catch (errorViews) {
        console.error('Failed to update views:', errorViews);
      }
    }
  };

  const getArticleViews = async () => {
    try {
      const res = await fetchFromClient(
        'GET',
        `${PATH_ARTICLE_VIEWS.ROOT}?articleId=${articleState.id}`
      );
      setArticleViewsData(res?.data?.attributes?.views || 0);
      setViews(res?.data?.attributes?.views || articleState?.attributes?.views || 0);
    } catch (errorViews) {
      console.error('Failed to update views:', errorViews);
    }
  };

  useEffect(() => {
    (async () => {
      if (location.search.includes('preview=true')) {
        const articleResponse = await fetchFromClient(
          'GET',
          `/articles?publicationState=preview&filters[publishedAt][$null]=true&filters[URL][$eq]=${encodeURIComponent(location.pathname)}&populate=author.Avatar,Hero_Image,VIDEOS_MOBILE,VIDEOS_DESKTOP,IMAGES_DESKTOP,IMAGES_MOBILE,tags.Name,OpenGraph.OGImage,HeaderTags,Form_Title,Form_Text,views`
        );

        if (articleResponse.data.length) {
          const img = getImages(articleResponse.data[0]);
          const vds = getVideos(articleResponse.data[0]);

          setArticleState(articleResponse.data[0]);
          setImagesState(img);
          setVideosState(vds);
          setIsUndefinedArticle(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (articleState && !Boolean(articleViewsData)) {
      getArticleViews();
    }
  }, []);

  useEffect(() => {
    const viewed_article = sessionStorage.getItem('viewed_article');
    const isPreview = location.search.includes('preview=true');

    if (
      !isPreview &&
      (Boolean(articleViewsData) || articleViewsData === 0) &&
      Number(viewed_article) !== Number(articleState.id)
    ) {
      updateArticleViewHandle();
    }
  }, [articleViewsData]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem('viewed_article', '');
    };
  }, []);

  const submitForm = async (response: any) => {
    setSubmitted(true);

    if (response.success) {
      const name = response.data?.name;
      const email = response.data?.email;
      const successUrl = `${PATH_SUCCESS.ROOT}?name=${encodeURI(name || '')}&email=${encodeURI(email || '')}`;

      await push(successUrl);
    }
  };

  const headerTags = {
    title: articleState?.attributes?.HeaderTags?.TitleTag,
    description: articleState?.attributes?.HeaderTags?.DescriptionTag,
    og: {
      title: articleState?.attributes?.OpenGraph?.OGTitle,
      description: articleState?.attributes?.OpenGraph?.OGDescription,
      image: articleState?.attributes?.OpenGraph?.OGImage?.data?.attributes.url,
    },
  };

  useEffect(() => {
    const isContentHeightMoreThanScreen1 = window.innerHeight;
    if (mainRef.current) {
      if (mainRef.current.offsetHeight / isContentHeightMoreThanScreen1 >= 2) {
        setIsScreenHeight(true);
      } else {
        setIsScreenHeight(false);
      }
    }
  }, [mainRef, articleState]);

  const is1Xl = useMediaQuery('(min-width: 1440px)');

  const formattedDate = articleState?.attributes.publishedAt
    ? new Date(articleState?.attributes.publishedAt).toLocaleString('en', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })
    : null;

  if (isUndefinedArticle) {
    return <NonExistedArticle originUrl={originUrl} />;
  }

  return (
    <>
      <Head>
        <meta key="viewport" name="viewport" content="initial-scale=1.0, width=device-width" />
        <title>{headerTags.title || 'CoSupport AI'}</title>
        <meta key="subject" name="subject" content={headerTags.title || 'CoSupport AI'} />
        <meta
          key="description"
          name="description"
          content={headerTags.description || 'CoSupport AI'}
        />
        <link key="canonical" rel="canonical" href={`${url}`} />
        <meta
          key="ogtitle"
          property="og:title"
          content={headerTags.og.title || headerTags.title || 'CoSupport AI'}
        />
        <meta key="ogtype" property="og:type" content="website" />
        <meta key="oglocale" property="og:locale" content="en_US" />
        <meta key="ogurl" property="og:url" content={`${url}`} />
        {headerTags.og.image ? (
          <meta key="ogimage" property="og:image" content={headerTags.og.image} />
        ) : null}
        <meta key="ogsitename" property="og:site_name" content={`${originUrl}/`} />
        <meta
          key="ogdescription"
          property="og:description"
          content={headerTags.og.description || headerTags.description || 'CoSupport AI'}
        />
        <script key="intlTelInputminjs" defer src="/js/intl-tel-input/js/intlTelInput.min.js" />
        <script
          key="organization-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{
              "@context": "https://schema.org/",
              "@type": "Article",
              "author": "${articleState?.attributes.author.data.attributes.Name}",
              "name": "${articleState?.attributes.Title}",
              "description": "${articleState?.attributes.Short_Description}",
              "interactionStatistic": [
                {
                  "@type": "InteractionCounter",
                  "interactionType": "https://schema.org/CommentAction",
                  "userInteractionCount": "${views || 1}"
                }
              ],
              "contentReferenceTime": "${formattedDate}"
            }`,
          }}
        />
        <Script
          id="googletagmanager3"
          key="googletagmanager3"
          strategy="beforeInteractive"
          src="https://www.googletagmanager.com/gtag/js?id=AW-11237889467"
        />
        <Script
          id="googletagmanager4"
          key="googletagmanager4"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-11237889467');
          `,
          }}
        />
        <Script
          id="googletagmanager5"
          key="googletagmanager5"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-WXHFTKL9');
          `,
          }}
        />
        <Script
          id="dealfront"
          key="dealfront"
          dangerouslySetInnerHTML={{
            __html: `(function(ss,ex){ window.ldfdr=window.ldfdr||function(){(ldfdr._q=ldfdr._q||[]).push([].slice.call(arguments));}; (function(d,s){ fs=d.getElementsByTagName(s)[0]; function ce(src){ var cs=d.createElement(s); cs.src=src; cs.async=1; fs.parentNode.insertBefore(cs,fs); }; ce('https://sc.lfeeder.com/lftracker_v1_'+ss+(ex?'_'+ex:'')+'.js'); })(document,'script'); })('JMvZ8gnv0VPa2pOd');`,
          }}
        />
        <Script
          id="apollo"
          key="apollo"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `!function(){var e,a;e=Math.random().toString(36).substring(7),(a=document.createElement("script")).src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+e,a.async=!0,a.defer=!0,a.onload=function(){window.trackingFunctions.onLoad({appId:"66bb4c503be771078edff8ab"})},document.head.appendChild(a)}();`,
          }}
        />
      </Head>
      <Header pathname={`${PATH_ARTICLES.ROOT}${articleState.attributes.URL}`} />
      <main ref={mainRef} className="max-1xl:!pb-0 md:!pt-[100px]">
        <div className="flex items-start">
          <div className="article-page flex-1 w-full">
            <div className="article-publish">
              <span className="content-tags">
                {articleState.attributes.tags.data.map((tag) => {
                  const tagUrl = tag.attributes.Name.toLowerCase().trim().replace(/\ /g, '-');

                  return (
                    <Link
                      key={`${articleState.id}-tag-${tag.id}`}
                      href={`${PATH_TAGS.ROOT}/${tagUrl}`}
                      className={`hover:[&>span]:bg-[#d8d8d8] [&>span]:transition-all duration-200 !no-underline`}
                    >
                      <span className="tag">{tag.attributes.Name}</span>
                    </Link>
                  );
                })}
              </span>
            </div>
            <h1 style={{ marginTop: '20px' }}>{articleState.attributes.Title}</h1>
            <div className="author-container gap-4">
              <AuthorInfo author={articleState.attributes.author} />
              <div className={'flex flex-row items-center justify-end gap-4 hidden md:flex'}>
                <div className="flex flex-row items-center">
                  <PublishDate publishDate={formattedDate} />
                </div>
                <div
                  className="flex items-center gap-2 truncate max-sm_mini:hidden justify-start views-counter-value"
                  title={`${views || 1} views`}
                >
                  <EyeIcon className="min-w-5.5" />
                  <span className="opacity-30 font-jamjuree truncate text-ebony font-medium text-[13px]">
                    {views || '1'}
                  </span>
                </div>
              </div>
              <div className="share">
                <a
                  target="_blank"
                  href={`https://twitter.com/intent/tweet?url=${url}`}
                  rel="noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 31 30"
                    fill="none"
                  >
                    <circle cx="15.5" cy="15" r="15" fill="#05050D" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.1424 9L10 9L14.3552 15.3682L10.0277 20.4896L12.0677 20.4896L15.2677 16.7025L17.8576 20.4896L22 20.4896L17.5571 13.9931L21.7762 9L19.7362 9L16.6446 12.6588L14.1424 9ZM15.3402 14.2025L12.7882 10.4711L13.3662 10.4711L15.6596 13.8245L15.3402 14.2025ZM18.6338 19.0186L16.2527 15.5368L16.5721 15.1588L19.2118 19.0186L18.6338 19.0186Z"
                      fill="white"
                    />
                  </svg>
                </a>
                <a
                  target="_blank"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                  rel="noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 31 30"
                    fill="none"
                  >
                    <circle cx="15.5" cy="15" r="15" fill="#1877F2" />
                    <path
                      d="M17.5946 10.537C16.5976 10.537 16.3142 10.977 16.3142 11.947L16.3142 13.548L18.9658 13.548L18.7045 16.1415L16.3137 16.1415L16.3137 23L13.1406 23L13.1406 16.141L11 16.141L11 13.5475L13.1416 13.5475L13.1416 11.9915C13.1416 9.375 14.1958 8 17.1529 8C17.7875 8 18.5468 8.05 19 8.113L19 10.548"
                      fill="white"
                    />
                  </svg>
                </a>
                <svg
                  onClick={() => navigator.clipboard.writeText(`${url}`)}
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  style={{ cursor: 'pointer' }}
                  className={'share-a-link'}
                >
                  <circle cx="12.5" cy="12.5" r="12" fill="white" />
                  <path
                    d="M14.748 16.0657L12.4853 18.3284C11.7027 19.111 10.7598 19.5022 9.65629 19.5022C8.55283 19.5022 7.6102 19.111 6.82843 18.3284C6.0459 17.5459 5.65463 16.6029 5.65463 15.4994C5.65463 14.396 6.0459 13.4534 6.82843 12.6716L9.09117 10.4088L10.2225 11.5402L7.9598 13.8029C7.48839 14.2743 7.25269 14.84 7.25269 15.5C7.25269 16.16 7.48839 16.7257 7.9598 17.1971C8.4312 17.6685 8.99689 17.9042 9.65685 17.9042C10.3168 17.9042 10.8825 17.6685 11.3539 17.1971L13.6167 14.9343L14.748 16.0657ZM10.7882 15.5L9.65685 14.3686L14.1823 9.84315L15.3137 10.9745L10.7882 15.5ZM15.8794 14.9343L14.748 13.8029L17.0108 11.5402C17.4822 11.0688 17.7179 10.5031 17.7179 9.84315C17.7179 9.18318 17.4822 8.61749 17.0108 8.14609C16.5394 7.67469 15.9737 7.43898 15.3137 7.43898C14.6537 7.43898 14.0881 7.67469 13.6167 8.14609L11.3539 10.4088L10.2225 9.27746L12.4853 7.01472C13.2678 6.23219 14.2108 5.84092 15.3143 5.84092C16.4177 5.84092 17.3604 6.23219 18.1421 7.01472C18.9247 7.79725 19.3159 8.74025 19.3159 9.84371C19.3159 10.9472 18.9247 11.8898 18.1421 12.6716L15.8794 14.9343Z"
                    fill="#758284"
                  />
                </svg>
                <p className="hidden md:flex">Share</p>
              </div>
            </div>
            <div className="image-block">
              <Image
                src={articleState?.attributes?.Hero_Image?.data?.attributes?.url}
                alt={articleState?.attributes?.Hero_Image?.data?.attributes?.alternativeText || ''}
                width={850}
                height={0}
              />
              <div className="absolute w-full flex md:hidden bottom-0 py-2.5 px-4 bg-ebony_opacity_50">
                <div className={'flex flex-row w-full items-center justify-between gap-4'}>
                  <div
                    className="flex items-center gap-2 truncate justify-start views-counter-value"
                    title={`${views || 1} views`}
                  >
                    <EyeIcon className="min-w-5.5 eye-icon" />
                    <span className="font-jamjuree truncate text-white font-medium text-[14px]">
                      {views || '1'}
                    </span>
                  </div>
                  <div className="flex flex-row items-center">
                    <PublishDate publishDate={formattedDate} />
                  </div>
                </div>
              </div>
            </div>
            <article>
              <Content
                //@ts-ignore
                content={articleState.attributes.Content}
                images={imagesState}
                videos={videosState}
                originUrl={originUrl}
              />
            </article>
            <div className="author-container second gap-4">
              <AuthorInfo
                author={articleState.attributes.author}
                showPublishDate={true}
                formattedDate={formattedDate}
              />
              <div className={'flex-row items-center hidden md:flex md:justify-end gap-4'}>
                <div className="flex flex-row items-center">
                  <PublishDate publishDate={formattedDate} />
                </div>
                <div
                  className="flex items-center gap-2 truncate max-sm_mini:hidden justify-start views-counter-value"
                  title={`${views || 1} views`}
                >
                  <EyeIcon className="min-w-5.5" />
                  <span className="opacity-30 font-jamjuree truncate text-ebony font-medium text-[13px]">
                    {views || '1'}
                  </span>
                </div>
              </div>
              <div className="share">
                <a
                  target="_blank"
                  href={`https://twitter.com/intent/tweet?url=${url}`}
                  rel="noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 31 30"
                    fill="none"
                  >
                    <circle cx="15.5" cy="15" r="15" fill="#05050D" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.1424 9L10 9L14.3552 15.3682L10.0277 20.4896L12.0677 20.4896L15.2677 16.7025L17.8576 20.4896L22 20.4896L17.5571 13.9931L21.7762 9L19.7362 9L16.6446 12.6588L14.1424 9ZM15.3402 14.2025L12.7882 10.4711L13.3662 10.4711L15.6596 13.8245L15.3402 14.2025ZM18.6338 19.0186L16.2527 15.5368L16.5721 15.1588L19.2118 19.0186L18.6338 19.0186Z"
                      fill="white"
                    />
                  </svg>
                </a>
                <a
                  target="_blank"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                  rel="noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 31 30"
                    fill="none"
                  >
                    <circle cx="15.5" cy="15" r="15" fill="#1877F2" />
                    <path
                      d="M17.5946 10.537C16.5976 10.537 16.3142 10.977 16.3142 11.947L16.3142 13.548L18.9658 13.548L18.7045 16.1415L16.3137 16.1415L16.3137 23L13.1406 23L13.1406 16.141L11 16.141L11 13.5475L13.1416 13.5475L13.1416 11.9915C13.1416 9.375 14.1958 8 17.1529 8C17.7875 8 18.5468 8.05 19 8.113L19 10.548"
                      fill="white"
                    />
                  </svg>
                </a>
                <svg
                  onClick={() => navigator.clipboard.writeText(`${url}`)}
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  className={'share-a-link'}
                  style={{ cursor: 'pointer' }}
                >
                  <circle cx="12.5" cy="12.5" r="12" fill="white" />
                  <path
                    d="M14.748 16.0657L12.4853 18.3284C11.7027 19.111 10.7598 19.5022 9.65629 19.5022C8.55283 19.5022 7.6102 19.111 6.82843 18.3284C6.0459 17.5459 5.65463 16.6029 5.65463 15.4994C5.65463 14.396 6.0459 13.4534 6.82843 12.6716L9.09117 10.4088L10.2225 11.5402L7.9598 13.8029C7.48839 14.2743 7.25269 14.84 7.25269 15.5C7.25269 16.16 7.48839 16.7257 7.9598 17.1971C8.4312 17.6685 8.99689 17.9042 9.65685 17.9042C10.3168 17.9042 10.8825 17.6685 11.3539 17.1971L13.6167 14.9343L14.748 16.0657ZM10.7882 15.5L9.65685 14.3686L14.1823 9.84315L15.3137 10.9745L10.7882 15.5ZM15.8794 14.9343L14.748 13.8029L17.0108 11.5402C17.4822 11.0688 17.7179 10.5031 17.7179 9.84315C17.7179 9.18318 17.4822 8.61749 17.0108 8.14609C16.5394 7.67469 15.9737 7.43898 15.3137 7.43898C14.6537 7.43898 14.0881 7.67469 13.6167 8.14609L11.3539 10.4088L10.2225 9.27746L12.4853 7.01472C13.2678 6.23219 14.2108 5.84092 15.3143 5.84092C16.4177 5.84092 17.3604 6.23219 18.1421 7.01472C18.9247 7.79725 19.3159 8.74025 19.3159 9.84371C19.3159 10.9472 18.9247 11.8898 18.1421 12.6716L15.8794 14.9343Z"
                    fill="#758284"
                  />
                </svg>
                <p className={'hidden md:flex'}>Share</p>
              </div>
            </div>
            {!submitted && (
              <section className="request-demo-form-section relative 1xl:hidden">
                <div className="request-demo-form-block">
                  <h2>{form.title}</h2>
                  <h3>{form.text}</h3>
                  <NewRequestDemoForm
                    tags={['Request Demo Article']}
                    id="requestDemoFormArticle"
                    onSubmit={submitForm}
                    name={'required'}
                    email={{
                      required: true,
                      validationPopupPosition: 'bottom',
                    }}
                    phone
                    tickets={{ required: true, title: 'Monthly ticket volume' }}
                    message
                    darkMode={false}
                    showSpinner={true}
                  />
                  <GreenButton form={'requestDemoFormArticle'} className="submit-button mt-[10px]">
                    <span>Request A Demo</span>
                  </GreenButton>
                </div>
              </section>
            )}
          </div>
          {!submitted && typeof isScreenHeight === 'boolean' && (
            <aside className={clsx('max-1xl:hidden sticky top-20')}>
              <div className="relative w-0">
                <div className="-translate-x-full ml-12.5 bg-light min-w-[340px] w-[19vw] px-4 py-5 rounded-lg">
                  <div className="request-demo-form-block">
                    <Typography
                      as="h5"
                      $type="PSmall"
                      className="font-formula text-center"
                      $weight="700"
                    >
                      {formDesktop.title}
                    </Typography>
                    <Typography
                      as="h6"
                      $type="PMini"
                      className="text-center font-jamjuree"
                      $weight="500"
                    >
                      {formDesktop.text}
                    </Typography>
                    <NewRequestDemoForm
                      size={'small'}
                      tags={['Request Demo Article']}
                      id="requestDemoFormArticle"
                      onSubmit={submitForm}
                      name={'required'}
                      email={{
                        required: true,
                        validationPopupPosition: 'bottom',
                      }}
                      phone
                      tickets={{ required: true, title: 'Monthly ticket volume' }}
                      message
                      messageTitle={'Any preferences for the demo'}
                      darkMode={false}
                      showSpinner={true}
                    />
                    <GreenButton
                      form={'requestDemoFormArticle'}
                      className="submit-button flex items-center justify-center w-full !min-w-full mt-2"
                      padding="py-3.5 px-0"
                    >
                      <Typography as="span" $type="PSmall" $weight="700">
                        Request A Demo
                      </Typography>
                    </GreenButton>
                  </div>
                </div>
              </div>
            </aside>
          )}
        </div>
      </main>
      {!error && articleState && is1Xl && (
        <div className="max-1xl:hidden overflow-hidden relative">
          <FullScreenQuidgetComponent
            separated
            loaderClassName="absolute !z-10"
            className="w-screen pt-[55px]"
          />
        </div>
      )}
      {relatedArticles && relatedArticles.length ? (
        <Container className={'w-full bg-ebony'}>
          <section className="related-articles [&_.views-counter-value>span]:text-white [&_.views-counter-value>svg>path]:fill-white">
            <h5 className="mb-7.5">You might be interested:</h5>
            <div className="articles">
              {relatedArticles.map((articleData) => (
                // @ts-ignore
                <Article key={articleData.id} article={articleData} />
              ))}
            </div>
          </section>
        </Container>
      ) : null}
      <Footer />
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  try {
    const data = await fetchFromServer('articles?fields[0]=URL');

    const articles: { attributes: { URL: string } }[] = data?.data || [];
    const paths = articles.map(({ attributes }) => ({
      params: { slug: attributes.URL.replace('/articles/', '') },
    }));

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (e) {
    return {
      paths: [],
      fallback: false,
    };
  }
}

export async function getStaticProps({ params = {} }: GetStaticPropsContext) {
  const originUrl: string = process.env.ORIGIN_URL || 'https://cosupport.ai';
  const { slug } = params;

  try {
    const articles = await fetchFromServer(
      `articles?filters[URL][$eq]=/articles/${slug}&populate=author.Avatar,Hero_Image,VIDEOS_MOBILE,VIDEOS_DESKTOP,IMAGES_DESKTOP,IMAGES_MOBILE,tags.Name,OpenGraph.OGImage,HeaderTags,Form_Title,Form_Text,views`
    );
    const article = articles.data[0];
    const relatedArticles = await fetchFromServer(
      `articles?sort=publishedAt:desc&pagination[page]=1&pagination[pageSize]=3&fields[0]=URL&fields[1]=Title&&fields[2]=publishedAt&fields[3]=views&populate=author.Avatar,Hero_Image,tags.Name&filters[id][$ne]=${article.id}`
    );
    const images = getImages(article);
    const videos = getVideos(article);

    return {
      props: {
        videos,
        article,
        images,
        originUrl,
        relatedArticles: relatedArticles.data,
      },
      revalidate: 60,
    };
  } catch (e) {
    return {
      props: {
        article: null,
        originUrl,
        error: { name: (e as Error).name, message: (e as Error).message },
      },
      revalidate: 60,
    };
  }
}
